/**
 * Bone2D rule from `Bone2D::get_configuration_warnings()` (skeleton_2d.cpp:412-427).
 * A missing Skeleton2D warns "chain should end at a Skeleton2D" under a Bone2D
 * parent and "only works with a Skeleton2D or another Bone2D" otherwise. An
 * all-zero `rest` warns that the bone lacks a rest pose.
 */
import { ruleRegistry } from '../../../linter/RuleRegistry.js';
import { armEmits, groundedArm, reportArm } from '../../../linter/ruleArms.js';
import { descendsFromClass } from '../../../godot/classdb/classBaseTypes.js';
import { knownParent, searchAncestors } from '../../../linter/parentType.js';
import { tupleComponent } from '../../../linter/validators/commonValidators.js';
import { makeFloatTupleRegex } from '../../../linter/validators/floatTupleValidator.js';
const arms = {
    chainDoesNotTerminate: groundedArm('bone2d-chain-does-not-terminate', { kind: 'configuration-warning' }),
    invalidParent: groundedArm('bone2d-invalid-parent', { kind: 'configuration-warning' }),
    missingRestPose: groundedArm('bone2d-missing-rest-pose', { kind: 'configuration-warning' }),
};
/**
 * Mirrors `Bone2D::_notification(NOTIFICATION_ENTER_TREE)` (skeleton_2d.cpp:99-113),
 * which resolves once (skeleton_2d.cpp:97-119): `parent_bone` is the immediate parent
 * cast to Bone2D, and `skeleton` walks up through Bone2D links to the first Skeleton2D
 * or non-bone. `descendsFromClass` stands in for `cast_to`, which accepts subclasses.
 */
function ancestryVerdict(scene, node) {
    const parent = knownParent(scene, node);
    if (parent.kind === 'root')
        return 'invalid-parent'; // parent_bone and skeleton both stay null
    if (parent.kind === 'unknowable')
        return 'unknowable';
    const parentIsBone2D = descendsFromClass(parent.parent.type, 'Bone2D');
    // The chain ends at the first ancestor that is neither, so an ancestor whose
    // class this file never states could be the Skeleton2D or the terminator.
    const search = searchAncestors(scene, node, (ancestor) => {
        if (descendsFromClass(ancestor.type, 'Skeleton2D'))
            return 'satisfied';
        if (!descendsFromClass(ancestor.type, 'Bone2D'))
            return 'chain-end';
        return undefined;
    });
    if (search.kind === 'unknowable')
        return 'unknowable';
    if (search.kind === 'found' && search.value === 'satisfied')
        return 'satisfied';
    return parentIsBone2D ? 'chain-broken' : 'invalid-parent';
}
const TRANSFORM2D_RE = makeFloatTupleRegex('Transform2D', 6);
/**
 * `rest == Transform2D(0, 0, 0, 0, 0, 0)`: `Transform2D::operator==`
 * (transform_2d.h:158-165) compares each column with exact `!=`, so this does too.
 * A malformed literal reports "not all-zero", since `linterParser.ts` owns format.
 */
function isAllZeroTransform2D(raw) {
    const match = TRANSFORM2D_RE.exec(raw);
    if (!match)
        return false;
    for (let i = 1; i <= 6; i++) {
        if (tupleComponent(match[i]) !== 0)
            return false;
    }
    return true;
}
function checkBone2D(context) {
    const { node, scene } = context;
    const diagnostics = [];
    switch (ancestryVerdict(scene, node)) {
        case 'chain-broken':
            reportArm(diagnostics, arms.chainDoesNotTerminate, node, `Bone2D '${node.name}' chains through Bone2D ancestors that never reach a Skeleton2D node. This Bone2D chain should end at a Skeleton2D node.`);
            break;
        case 'invalid-parent':
            reportArm(diagnostics, arms.invalidParent, node, `Bone2D '${node.name}' only works with a Skeleton2D or another Bone2D as parent node.`);
            break;
        default:
            break;
    }
    const rest = node.rawProperties.rest;
    // `rest` is declared `Transform2D rest;` (skeleton_2d.h:48), but `Bone2D::Bone2D()`
    // zeroes all three columns (skeleton_2d.cpp:496-499). All-zero is the class default
    // the serialiser omits, so an absent key is the state the warning exists for.
    if (rest === undefined || isAllZeroTransform2D(rest)) {
        reportArm(diagnostics, arms.missingRestPose, node, `Bone2D '${node.name}' has no rest pose: its rest is the all-zero Transform2D, which is what an unset one stores. Go to the Skeleton2D node and set one.`);
    }
    return diagnostics;
}
const bone2DAncestryRule = {
    meta: {
        name: 'valid-bone2d-ancestry',
        description: "Warns when a Bone2D's ancestor chain never reaches a Skeleton2D, its immediate parent is neither a Skeleton2D nor a Bone2D, or its rest pose is the all-zero Transform2D",
        category: 'validation',
        applicableNodeTypes: ['Bone2D'],
        emits: armEmits(arms),
    },
    check: checkBone2D,
};
ruleRegistry.register(bone2DAncestryRule);
//# sourceMappingURL=linter.js.map